import { socketPayroll } from '@nxsys/services/sockets';
import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

export type SocketEvent = {
  type: string;
  success?: boolean;
  message?: string;
  data?: any;
  progress?: number;
  isDataHasErrors?: boolean;
  error?: {
    message: string;
  };
};

export type UseSocketOptions = {
  eventName: string;
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  onSettled?: () => void;
};

export type UsePayrollSocketReturn = {
  /** Ref that becomes true once the socket event is received. Reset before each API call. */
  receivedRef: MutableRefObject<boolean>;
  /** Reconnect socket if disconnected, call before waiting for events. */
  ensureConnected: () => void;
};

export const usePayrollSocket = (
  options: UseSocketOptions,
): UsePayrollSocketReturn => {
  const {
    eventName,
    onSuccess,
    onError,
    onSettled,
  } = options;

  const receivedRef = useRef(false);

  const handleSocketEvent = useCallback(
    (eventData: SocketEvent) => {
      receivedRef.current = true;
      if (eventData?.success) {
        onSuccess?.(eventData);
      } else {
        // BE payroll jobs emit the human-readable reason on the top-level
        // `message` field (e.g. the PAL "period already paid" error), not
        // `error.message`. Prefer it so the reason reaches the UI.
        onError?.(eventData?.message || eventData?.error?.message || '');
      }
      onSettled?.();
    },
    [onSuccess, onError],
  );

  const ensureConnected = useCallback(() => {
    if (!socketPayroll.connected) {
      socketPayroll.connect();
    }
  }, []);

  useEffect(() => {
    socketPayroll.on(eventName, handleSocketEvent);

    return () => {
      socketPayroll.off(eventName, handleSocketEvent);
    };
  }, [eventName, handleSocketEvent]);

  return { receivedRef, ensureConnected };
};

export default usePayrollSocket;
